import React from 'react'

import {
  getAttackStageReadable,
  isInHaltingStage
} from 'components/attacks/utils/attack-stages'
import { AttackTypeIcon } from 'components/common/AttackTypeIcon'
import { HeptagonGlyphIcon } from 'components/common/GlyphIcon'
import {
  AttackDescription,
  AttackRow,
  TargetsColumn,
  Status,
  HaltButton,
  DetailsChevron
} from 'components/navigation/Notifications/AttackRows/common/AttackRow'
import { KubernetesTargets } from 'components/navigation/Notifications/AttackRows/common/Targets'
import useMetadata from 'hooks/use-metadata'
import { useGetKubernetesTargetsKinds } from 'queries/kubernetes/use-get-kubernetes-targets-kinds'
import { K8sAttack } from 'types/api/kubernetes/attacks'

export const targetCountByKind = (targets, k8sKinds) => {
  const kinds = targets.reduce((kinds, { kind }) => {
    if (kinds[kind]) {
      kinds[kind] += 1
    } else {
      kinds[kind] = 1
    }
    return kinds
  }, {})
  const kindKeysToDisplayWithCount = k8sKinds.filter((kind) => kind in kinds)
  return kindKeysToDisplayWithCount.map((kind) => ({
    name: kind,
    count: kinds[kind]
  }))
}

export const KubernetesAttackRow: React.FC<{
  attack: K8sAttack
  doFetch(): void
  clickableRow?: boolean
  selectableRow?: boolean
  onRowSelected?(): void
  onHaltAttack?(): void
  showHaltButton?: boolean
  checked?: boolean
}> = ({
  attack,
  doFetch,
  clickableRow,
  selectableRow,
  onRowSelected,
  onHaltAttack,
  showHaltButton,
  checked
}) => {
  const { getAttackName } = useMetadata()
  const { data: k8sKinds = [] } = useGetKubernetesTargetsKinds()

  const { infraAttack = {} } = attack
  const hasInfraAttack = Boolean(infraAttack?.guid)
  const args =
    attack?.attack?.impactDefinition?.cliArgs ??
    infraAttack?.infra_command?.commandArgs?.cliArgs ??
    []
  const targets = attack?.attack?.targetDefinition?.strategy?.k8sObjects ?? []
  const kindCounts = targetCountByKind(targets, k8sKinds)
  const attackId = hasInfraAttack ? infraAttack.guid : attack.uid
  const stage = hasInfraAttack ? infraAttack.stage : attack.stage
  const isHalting = isInHaltingStage(stage)

  return (
    <AttackRow
      data-active-attack-row
      checked={checked}
      clickableRow={clickableRow}
      selectableRow={selectableRow}
      showHaltButton={showHaltButton}
      to={{
        pathname: `/attacks/kubernetes/${attack.uid}`,
        state: { k8Attack: attack, infraAttack: infraAttack }
      }}
      onRowSelected={onRowSelected}
    >
      <AttackDescription
        description={
          isHalting ? (
            'Halting...'
          ) : (
            <Status>
              {getAttackStageReadable(
                hasInfraAttack ? infraAttack.stage : attack.stage
              )}
              ...
            </Status>
          )
        }
        icon={
          <HeptagonGlyphIcon>
            <AttackTypeIcon icon={args[0]} />
          </HeptagonGlyphIcon>
        }
        name={getAttackName(args[0])}
      />
      <TargetsColumn>
        <KubernetesTargets targets={kindCounts} />
      </TargetsColumn>
      {showHaltButton && (
        <HaltButton
          fetchAttack={doFetch}
          isHalting={isHalting}
          trackingOptions={{ AttackId: attackId }}
          onConfirmRequest={onHaltAttack}
        />
      )}
      {clickableRow && <DetailsChevron />}
    </AttackRow>
  )
}
